import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import StatusBadge from "../../components/common/StatusBadge";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/common/Pagination";
import SearchBar from "../../components/common/Search";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown";
import ActionMenu from "../../components/common/ActionMenu";
import { AiOutlineEye } from "react-icons/ai";
import { formatDate } from "../../utils/HelperMethod";
import { useAdminSupportTickets } from "../../custom-hooks/queries";
import CustomToggleFilter from "../../components/common/CustomToggleFilter";

// 🔹 Filters
const statusOptions = [
  { id: "Pending", name: "Pending" },
  { id: "Approved", name: "Approved" },
  { id: "Suspended", name: "Suspended" },
  { id: "Rejected", name: "Rejected" },
];

const priorityOptions = [
  { id: "Low", name: "Low" },
  { id: "Medium", name: "Medium" },
  { id: "High", name: "High" },
  { id: "Urgent", name: "Urgent" },
];

const typeOptions = [
  { id: "user", name: "User" },
  { id: "vendor", name: "Vendor" },
];

export default function SupportTicket() {
  const navigate = useNavigate();

  // 🔹 State (same pattern)
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState("user"); // default user
  const [searchText, setSearchText] = useState("");

  const pageSize = 20;
  const handleTypeToggleChange = (value) => {
    const typeValue = value === "user" ? "user" : "vendor";
    setTypeFilter(typeValue);
    setCurrentPage(1);
  };

  // 🔹 API Query
  const { data, isLoading, isError } = useAdminSupportTickets({
    page: currentPage,
    pageSize,
    search: searchText,
    type: typeFilter,
    status: statusFilter,
    priority: priorityFilter,
  });

  // 🔹 Map API response
  const tickets =
    data?.data?.tickets?.map((item) => ({
      ticketCode: item.ticketCode,
      ticketId: item.ticketId,
      subject: item.issue,
      type: item.type,
      priority: item.priority,
      status: item.status,
    })) || [];

  // 🔹 Actions
  const ticketActions = (row) => [
    {
      label: "View Ticket",
      icon: <AiOutlineEye />,
      onClick: () => navigate(`/admin/support-details/${row.ticketId}`),
    },
  ];

  // 🔹 Table Columns
  const columns = [
    {
      key: "ticketId",
      label: "TICKET ID",
      width: "1.4fr",
      align: "left",
      render: (row) => (
        <div className="font-semibold text-gray-900 truncate">
          {row.ticketCode}
        </div>
      ),
    },
    {
      key: "subject",
      label: "SUBJECT",
      width: "1fr",
      align: "left",
      render: (row) => (
        <div className="text-gray-700 truncate">{row.subject}</div>
      ),
    },
    {
      key: "userName",
      label: "USER / VENDOR",
      width: "1fr",
      align: "center",
      render: (row) => <div className="text-gray-700 truncate">{row.type}</div>,
    },

    {
      key: "priority",
      label: "PRIORITY",
      width: "1fr",
      align: "center",
      render: (row) => row.priority,
    },
    {
      key: "status",
      label: "STATUS",
      width: "1.2fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },

    {
      key: "actions",
      label: "ACTIONS",
      width: "0.8fr",
      align: "center",
      render: (row) => <ActionMenu actions={ticketActions(row)} />,
    },
  ];

  // 🔹 Handlers
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <PageHeader
        title="Support Tickets"
        description="Manage user and vendor support tickets"
      />

      <div className="bg-white rounded-[12px] border border-[#D9D9D9] shadow-sm overflow-hidden mt-4 sm:mt-6">
        {/* 🔍 Search + Filters */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 py-6 sm:py-10 px-4 sm:px-6">
          <div className="w-1/2">
            <SearchBar onSearch={handleSearch} />
            <div className="flex flex-col gap-2 w-1/2">
              <span className="mt-2">
                <CustomToggleFilter
                  options={[
                    { label: "User", value: "user" },
                    { label: "Vendor", value: "vendor" },
                  ]}
                  active={typeFilter}
                  onChange={handleTypeToggleChange}
                />
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <CustomFilterDropdown
              options={statusOptions}
              onChange={setStatusFilter}
              placeholder="Filter by Status"
              valueKey="id"
              labelKey="name"
            />

            <CustomFilterDropdown
              options={priorityOptions}
              onChange={setPriorityFilter}
              placeholder="Filter by Priority"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>

        {/* 📋 Table */}
        {isError ? (
          <div className="flex justify-center items-center py-20 text-red-500">
            Failed to load support tickets.
          </div>
        ) : (
          <Table
            columns={columns}
            data={tickets}
            isLoading={isLoading}
            skeletonRows={pageSize}
          />
        )}
      </div>

      {/* 📄 Pagination */}
      {!isLoading && !isError && data?.data?.totalPages > 1 && (
        <div className="mt-4 sm:mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={data?.data?.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
